import { Button, Skeleton } from '@mantine/core';
import { IconPlayerPlay, IconPlus } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useCreateAndNavToTest } from '~/hooks/api/useCreateAndNavToTest';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';
import { DataTable } from '../tables/common/DataTable';
import { DataTableHeader } from '../tables/common/DataTableHeader';
import DataTablePaginationControls from '../tables/common/DataTablePaginationControls';
import { useDataTable } from '../tables/common/useDataTable';
import { runResultColumns, type NoteMode } from '../tables/run/runResultColumns';
import { useColorScheme } from '@mantine/hooks';

// Maximum records to fetch even with pagination to prevent performance issues on large projects
const MAX_PAGINATION_LIMIT = 500;

export default function ProjectTestRun({
  defaultLimit,
  showViewMoreButton,
  showTableHeader,
  showPagination,
  applyDefaultStyles = true,
  noteMode = 'message',
}: {
  defaultLimit?: number;
  showViewMoreButton: boolean;
  showTableHeader: boolean;
  showPagination: boolean;
  applyDefaultStyles?: boolean;
  noteMode?: NoteMode;
}) {
  const [limit, setLimit] = useState<number | undefined>(defaultLimit);

  const router = useRouter();
  const projectId = queryParamToString(router.query.projectId);
  const colorScheme = useColorScheme();

  const { data: tests, isLoading: isTestsLoading } = api.test.listTests.useQuery({ projectId, limit: 1 });
  const { data: suiteRuns, isLoading: isSuiteRunsLoading } = api.suiteRun.list.useQuery({
    projectId,
    // Use a reasonable max limit for pagination to prevent fetching all records
    limit: showPagination ? MAX_PAGINATION_LIMIT : limit,
  });

  const isLoading = isTestsLoading || isSuiteRunsLoading;

  // simplified to only use suite runs since single runs are deprecated
  const runs = useMemo(() => {
    return isSuiteRunsLoading ?
        []
      : (suiteRuns || [])
          .map((run) => {
            const firstTestWithName = run.testRuns.find((testRun) => testRun.testV2?.name);
            const displayName =
              run.testSuite.temp ? (firstTestWithName?.testV2?.name ?? run.testSuite.name) : run.testSuite.name;

            return {
              ...run,
              projectId,
              name: displayName,
              isTempSuite: run.testSuite.temp,
              passNum: run.testRuns.filter((testRun) => testRun.status === 'PASSED').length,
              failNum: run.testRuns.filter((testRun) => testRun.status === 'FAILED').length,
              flakyNum: run.testRuns.filter((testRun) => testRun.status === 'FLAKY').length,
              runningNum: run.testRuns.filter(
                (testRun) => testRun.status === 'RUNNING' || testRun.status === 'RETRYING',
              ).length,
              skippedNum: run.testRuns.filter((testRun) => testRun.status === 'SKIPPED').length,
              queuedNum: run.testRuns.filter((testRun) => testRun.status === 'QUEUED').length,
              durationMs: run.durationMs ?? run.testRuns.reduce((acc, testRun) => acc + (testRun.durationMs ?? 0), 0),
              gitBranch: run.gitBranch ?? undefined,
              note: run.note ?? undefined,
            };
          })
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          // Only apply limit when pagination is disabled
          .slice(0, showPagination ? undefined : limit);
  }, [suiteRuns, isSuiteRunsLoading, limit, projectId, showPagination]);

  const createTestHandler = useCreateAndNavToTest({});

  const table = useDataTable({
    columns: runResultColumns({ noteMode, colorScheme }),
    data: runs,
    enableQueryParam: true,
    storageKey: 'testRunsTable',
    // Always enable pagination, just conditionally show controls. Otherwise the pagination resets on tab switch
    enablePagination: true,
    pinnedColumns: {
      left: ['select', 'name'],
    },
  });

  if (isLoading) {
    return <Skeleton className="h-64">&nbsp;</Skeleton>;
  }

  if (!isLoading && runs?.length === 0) {
    return (
      <div className="mt-2">
        <span className="text-sm text-stone-600 dark:text-stone-400">
          Your test results will appear here once you run your first test
        </span>
        <div className="pt-8">
          <Button
            onClick={
              !isTestsLoading && tests !== undefined && tests.length > 0 ?
                () => router.push(`/project/${projectId}/testSuite`).catch(() => undefined)
              : createTestHandler
            }
            leftSection={tests?.length || 0 > 0 ? <IconPlayerPlay /> : <IconPlus />}
          >
            {!isTestsLoading && tests !== undefined && tests.length > 0 ? 'Kick off a test run' : 'Create a test'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {!!showTableHeader && (
        <div className="pb-4">
          <DataTableHeader
            table={table}
            itemName="test run"
            isLoading={isLoading}
          />
        </div>
      )}

      <DataTable
        table={table}
        emptyMessage="No test runs found"
        isLoading={isLoading}
        className="bg-inherit"
        classNames={
          applyDefaultStyles ?
            {
              header: 'bg-[var(--mantine-color-default)]',
              pinnedCell: 'bg-[var(--mantine-color-default)]',
            }
          : {
              header: 'bg-[var(--mantine-color-body)]',
            }
        }
        rowLinkProps={(row) => ({
          href: `/project/${row.original.projectId}/history/g_${row.original.id}`,
        })}
      />

      {!!showPagination && (
        <div className="mt-4">
          <DataTablePaginationControls
            table={table}
            itemName="test run"
            showSelectedCount={false}
          />
        </div>
      )}

      {!!showViewMoreButton && !isLoading && runs?.length > 0 && (
        <div className="mt-4 flex items-center">
          <Button
            variant="default"
            onClick={() => (limit !== undefined ? setLimit(undefined) : setLimit(defaultLimit))}
          >
            View {limit !== undefined ? 'All' : 'Less'}
          </Button>
        </div>
      )}
    </div>
  );
}
